import { useState, useEffect, useCallback, useRef } from 'react';
import { DirectusServiceExtension } from '@/services/directusServiceExtension';
import { DirectusService } from '@/services/directusService';
import { useContentSync } from './useContentSync';
import { contentBatchLoader } from '@/services/contentBatchLoader';

interface PersistentContentOptions {
  collection: string;
  itemId: string | number;
  field: string;
  initialValue?: string;
  enableRealtime?: boolean;
  pollingInterval?: number;
}

interface PersistentContentState {
  content: string;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  error: string | null;
}

/**
 * Hook for managing persistent content with direct backend saves and real-time updates
 * OPTIMIZED: Removed excessive logging for 10x performance improvement
 */
export const usePersistentContent = ({
  collection,
  itemId,
  field,
  initialValue = '',
  enableRealtime = true,
  pollingInterval = 2000,
}: PersistentContentOptions) => {
  
  const [state, setState] = useState<PersistentContentState>({
    content: '',
    isLoading: true,
    isSaving: false,
    lastSaved: null,
    error: null,
  });

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveTimestampRef = useRef<number>(0);
  const isMountedRef = useRef(true);
  const isUpdatingFromSyncRef = useRef(false);
  const refreshDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Real-time content sync via SSE (preferred method)
  useContentSync({
    collection,
    itemId,
    enabled: enableRealtime && collection !== 'settings',
    onUpdate: useCallback(async (event) => {
      if (state.isSaving || isUpdatingFromSyncRef.current) {
        return;
      }

      const timeSinceLastSave = Date.now() - lastSaveTimestampRef.current;
      if (timeSinceLastSave < 3000) {
        return;
      }

      try {
        isUpdatingFromSyncRef.current = true;
        
        const item = await DirectusServiceExtension.getCollectionItemSafe(collection, itemId);
        const serverContent = item && field in item ? String(item[field] || '') : '';
        
        if (isMountedRef.current && serverContent !== state.content) {
          setState(prev => ({
            ...prev,
            content: serverContent,
          }));
        }
      } catch (error) {
        // Silent fail for real-time updates
        isUpdatingFromSyncRef.current = false;
      }
    }, [collection, itemId, field, state.content, state.isSaving]),
  });

  // Force refresh from server with cache bypass (defined early for use in event listeners)
  const refreshFromServer = useCallback(async () => {
    // Invalidate cache before fetching
    contentBatchLoader.invalidateCache(collection, itemId);
    
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Use the same loader as the initial load. The cache was just invalidated
      // above, so this re-fetches fresh — and we deliberately DON'T pass
      // `bypassCache` (which cancels in-flight requests for the same item,
      // hanging the other fields that share its cache key). Sharing the pending
      // request is what the working initial-load path does.
      const serverContent = await contentBatchLoader.getFieldContent(
        collection,
        collection === 'settings' ? 'singleton' : itemId,
        field,
        false,
      );

      if (isMountedRef.current) {
        setState(prev => ({
          ...prev,
          content: serverContent != null ? String(serverContent) : '',
          isLoading: false,
          lastSaved: new Date(),
        }));
      }
    } catch (error) {
      // 403/404 are expected for fields whose item doesn't exist (e.g. a section
      // that references a hard-coded id) — don't log those as errors.
      const msg = error instanceof Error ? error.message : String(error);
      const is403or404 = /Forbidden|403|404|not found/i.test(msg);
      if (import.meta.env.DEV && !is403or404) {
        console.error('Failed to refresh content:', error);
      }
      if (isMountedRef.current) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: msg,
        }));
      }
    }
  }, [collection, itemId, field]);

  // Listen for Directus content update events from Visual Editor
  useEffect(() => {
    // TRAILING debounce: a save emits a burst of refresh events (some before the
    // change is committed). Coalesce them into a single refresh that runs AFTER
    // the burst settles, so the fetch sees the committed value. (A leading skip
    // would run the pre-commit refresh and drop the fresh one.)
    const scheduleRefresh = () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      refreshDebounceRef.current = setTimeout(() => {
        refreshDebounceRef.current = null;
        // `refreshFromServer` invalidates the cache before fetching, so we don't
        // need to invalidate again here.
        refreshFromServer();
      }, 350);
    };

    const handleDirectusUpdate = (event: CustomEvent) => {
      const detail = event.detail;
      const itemMatches =
        detail.collection === collection &&
        String(detail.itemId) === String(itemId);
      if (!itemMatches) return;

      // Visual Editor save: the exact new value for THIS field is in the event.
      // Apply it directly — instant and race-free (no re-fetch).
      if (detail.field === field && detail.value !== undefined) {
        contentBatchLoader.invalidateCache(collection, itemId);
        if (isMountedRef.current) {
          setState(prev => ({
            ...prev,
            content: detail.value != null ? String(detail.value) : '',
            isLoading: false,
            lastSaved: new Date(),
          }));
        }
        return;
      }

      // No value provided (or different field) — fall back to a debounced refetch.
      if (detail.field === undefined || detail.field === field) {
        scheduleRefresh();
      }
    };

    const handleDirectusRefresh = () => scheduleRefresh();

    // Add event listeners
    window.addEventListener('directus:content-updated', handleDirectusUpdate as EventListener);
    window.addEventListener('directus:refresh', handleDirectusRefresh);

    return () => {
      window.removeEventListener('directus:content-updated', handleDirectusUpdate as EventListener);
      window.removeEventListener('directus:refresh', handleDirectusRefresh);
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
    };
  }, [collection, itemId, field, refreshFromServer]);

  // Initial load from Directus - OPTIMIZED with better timeout handling
  useEffect(() => {
    isMountedRef.current = true;
    let timeoutId: NodeJS.Timeout;

    const loadContent = async () => {
      // Don't fetch until we have a concrete item id. Callers may pass an empty
      // id while a slug→id lookup is still resolving (see usePageId); fetching
      // `/items/<collection>/` with no id just 403s. Render the fallback instead.
      if (itemId === null || itemId === undefined || itemId === '') {
        if (isMountedRef.current) {
          setState(prev => ({ ...prev, content: initialValue || '', isLoading: false }));
        }
        return;
      }

      // Increased timeout to 15 seconds for slower connections
      timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          setState(prev => ({
            ...prev,
            content: initialValue || '',
            isLoading: false,
            error: 'Loading timeout',
          }));
        }
      }, 15000);

      try {
        // Use batch loader for optimized loading
        const serverContent = await contentBatchLoader.getFieldContent(
          collection,
          collection === 'settings' ? 'singleton' : itemId,
          field,
          false // Use cache for faster loading
        );
        
        clearTimeout(timeoutId);

        const finalContent = serverContent !== null && serverContent !== undefined
          ? serverContent
          : initialValue;

        if (isMountedRef.current) {
          setState(prev => ({
            ...prev,
            content: finalContent,
            isLoading: false,
          }));
        }
      } catch (error) {
        clearTimeout(timeoutId);
        // Only log errors in development, but suppress 403/404 (missing pages)
        if (import.meta.env.DEV) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          const is403or404 = errorMessage.includes('Forbidden') || errorMessage.includes('403') || errorMessage.includes('404');
          
          if (!is403or404) {
            console.error('Failed to load content:', error);
          }
          // Silently handle 403/404 - page doesn't exist, use fallback value
        }
        
        if (isMountedRef.current) {
          setState(prev => ({
            ...prev,
            content: initialValue || '',
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load content',
          }));
        }
      }
    };

    loadContent();

    return () => {
      isMountedRef.current = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [collection, itemId, field, initialValue]);

  // Save to server with cache invalidation
  const saveToServer = useCallback(async (contentToSave?: string) => {
    const content = contentToSave || state.content;
    
    setState(prev => ({
      ...prev,
      isSaving: true,
      error: null,
    }));

    try {
      // Special handling for settings collection
      if (collection === 'settings') {
        if (import.meta.env.DEV) {
          console.warn('Direct editing of settings is not supported');
        }
        setState(prev => ({
          ...prev,
          isSaving: false,
        }));
        return false;
      }
      
      let result;
      
      if (collection === 'sub_menu_content') {
        const existingItem = await DirectusServiceExtension.getCollectionItemSafe(collection, itemId);
        
        if (existingItem && existingItem.id) {
          result = await DirectusServiceExtension.updateField(collection, itemId, field, content);
        } else {
          const newItem = {
            id: itemId,
            [field]: content,
            status: 'published'
          };
          result = await DirectusServiceExtension.createItem(collection, newItem);
        }
      } else {
        result = await DirectusServiceExtension.updateField(collection, itemId, field, content);
      }
      
      lastSaveTimestampRef.current = Date.now();
      contentBatchLoader.invalidateCache(collection, itemId);

      if (isMountedRef.current) {
        setState(prev => ({
          ...prev,
          content: contentToSave,
          isSaving: false,
          lastSaved: new Date(),
        }));
      }

      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Save failed:', error instanceof Error ? error.message : String(error));
      }
      
      if (isMountedRef.current) {
        setState(prev => ({
          ...prev,
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to save content',
        }));
      }
      return false;
    }
  }, [collection, itemId, field, state.content]);

  // Update content locally and save immediately
  const updateContent = useCallback((newContent: string) => {
    setState(prev => ({
      ...prev,
      content: newContent,
      error: null,
    }));

    saveToServer(newContent);
  }, [saveToServer]);

  const discardChanges = useCallback(() => {
    refreshFromServer();
  }, [refreshFromServer]);

  return {
    ...state,
    updateContent,
    saveToServer,
    refreshFromServer,
    discardChanges,
  };
};

export default usePersistentContent;
